/**
 * What colour a fleet cell is, and why.
 *
 * A pure function in its own module because the predecessor's equivalent lived
 * inline in a 12,000-line HTML file, in TWO places that disagreed:
 * `fleetCellClass` never tested storage or recording while `cardBorderClass`,
 * 100 lines below, always had. A failed SD card — and a silent SD failure, the
 * flagship fault this product exists to detect — rendered GREEN in the grid and
 * RED on the card at the same moment.
 *
 * Five cameras carried that fault when it was found on 2026-08-12, and four of
 * them happened to be amber already for an unrelated 30-day uptime reason. They
 * would have turned green the moment their uptime recovered. The defect was
 * masked by a coincidence, which is why reading the code found it and looking
 * at the screen did not.
 */

export type Outcome = 'pass' | 'fail' | 'unknown' | 'unavailable';

export type CellState =
  | 'ok'
  | 'fault'
  | 'warning'
  /** No current reading. Honest grey — not green. */
  | 'stale'
  /** Cannot be checked at all: the driver does not declare the capability. */
  | 'unmonitored'
  | 'maintenance'
  | 'retired';

/**
 * WHICH fault, not just how bad it is.
 *
 * Added for the site summary line. A site card has to say "2 of 2 not
 * answering" rather than showing a red edge, and counting reasons by parsing
 * the `reason` sentence would be a second, silent implementation of this
 * function that drifts the first time a wording changes. The kind is the
 * discriminator; `state` and `reason` are both derived from it.
 *
 * It is also what `/api/fleet`'s ranking mirrors in SQL — see the query there,
 * and `test/fleet_rank.test.ts`, which pins the two against each other.
 */
export type CellKind =
  | 'ok'
  | 'unreachable'
  | 'no_video'
  | 'storage_fault'
  | 'not_recording'
  /** A host agent's own checks: an array, a disk, an expected service, a full filesystem. */
  | 'host_fault'
  /** A host's system drive nearly full (073): room to act, so amber. */
  | 'host_warning'
  | 'critical_alert'
  | 'warning_alert'
  | 'unmonitored'
  | 'stale'
  | 'maintenance'
  | 'retired';

export const KIND_STATE: Record<CellKind, CellState> = {
  ok: 'ok',
  unreachable: 'fault',
  no_video: 'fault',
  storage_fault: 'fault',
  not_recording: 'fault',
  host_fault: 'fault',
  host_warning: 'warning',
  critical_alert: 'fault',
  warning_alert: 'warning',
  unmonitored: 'unmonitored',
  stale: 'stale',
  maintenance: 'maintenance',
  retired: 'retired',
};

export interface AssetChecks {
  [checkKey: string]: { outcome: Outcome; valueText?: string | null; reason?: string | null } | undefined;
}

export interface CellInput {
  lifecycle: string;
  checks: AssetChecks;
  openAlertSeverities: readonly string[];
  /**
   * Is this deployment meant to record onto the device's own storage?
   *
   * `assets.records_onboard`, carried down with the fleet. Optional and
   * defaulting to true, matching the column's default, so a caller that has not
   * been taught about it cannot silence anything.
   *
   * 🔴 This screen was the last surface that did not know. Migration 019 ported
   * the predecessor's exemption and `src/alerts/engine.ts:160` suppresses
   * NOT_RECORDING on it, which closed 13 alerts on 2026-08-27 — but the CHECK
   * still runs and still returns `fail`, because the exemption describes the
   * DEPLOYMENT and not the device (several of these units still have a card in
   * from a previous site). So the grid went on painting those cameras red while
   * the alert list correctly held nothing about them: the same two-surfaces-
   * disagreeing defect this module was written to end, arriving from the other
   * direction.
   *
   * Handled by masking the two recording checks to `unavailable`, which is
   * already the state for "this question cannot be asked here" and already has
   * a colour. An exempt camera is not green and not red; it is not checkable
   * for recording, exactly like an Axis camera with no storage API.
   */
  recordsOnboard?: boolean;
}

export interface CellVerdict {
  state: CellState;
  /** Which fault, for counting. See `CellKind`. */
  kind: CellKind;
  /** Why, in words an operator can act on. Never empty for a non-ok state. */
  reason: string;
}

/** Checks that speak to whether footage is actually being produced and kept. */
/** `nx_camera_recording` (070) is a camera NX records: NX's own word on it. */
const RECORDING_CHECKS = ['storage_health', 'recording_fresh', 'nx_camera_recording'] as const;

/** A host agent's own checks, in the order a fault is worth naming. See `src/agent/readings.ts`. */
const HOST_CHECKS = ['array_health', 'disk_health', 'storage_capacity', 'services_running'] as const;

/** Words for a failing host check that arrived without a reason. */
const HOST_CHECK_WORDS: Record<(typeof HOST_CHECKS)[number], string> = {
  array_health: 'a RAID set or pool is not healthy',
  disk_health: 'a disk is failing',
  storage_capacity: 'a filesystem is nearly full',
  services_running: 'an expected service is not running',
};

function verdict(kind: CellKind, reason: string): CellVerdict {
  return { kind, state: KIND_STATE[kind], reason };
}

export function fleetCellState(input: CellInput): CellVerdict {
  if (input.lifecycle === 'maintenance') {
    return verdict('maintenance', 'in maintenance');
  }
  if (input.lifecycle === 'retired') {
    return verdict('retired', 'retired');
  }
  if (input.lifecycle !== 'active') {
    /*
     * A provisioning asset is not polled, so whatever state it carries is
     * frozen at the moment it was created. Painting that green claims health on
     * evidence nobody has gathered.
     *
     * `off_hire` lands here too, and correctly: warehouse stock is unpowered,
     * so its silence is health and carries no information at all.
     */
    return verdict('stale', `${input.lifecycle}: not checked`);
  }

  const reachable = input.checks['reachable'];
  if (reachable === undefined || reachable.outcome === 'unknown') {
    /*
     * No answer on the direct connection is grey: one path failing is as
     * likely the path as the camera. But not when a second, independent path
     * agrees — NX on site saying it cannot reach the camera either (070) —
     * and not once the silence has raised its critical alert: grey there hid
     * a dead camera all night (Sandy Point C06, 2026-09-30).
     */
    const nx = input.checks['nx_camera_recording'];
    if (reachable !== undefined && nx?.outcome === 'unknown' && nx.valueText === 'Offline') {
      return verdict('unreachable', 'no answer, and NX says offline');
    }
    if (reachable !== undefined && input.openAlertSeverities.includes('critical')) {
      return verdict('critical_alert', 'no current reading; open critical alert');
    }
    return verdict('stale', 'no current reading');
  }
  if (reachable.outcome === 'fail') {
    return verdict('unreachable', 'not answering');
  }

  /*
   * A host agent's checks (PLAN-2026-09-15 §3D). Read here, before anything
   * camera-shaped, because nothing below knows them: until 2026-09-22 the
   * Proxmox node sat green in this grid — "all checks passing" — with both its
   * pools DEGRADED and a dead NVMe, because this function only ever asked
   * camera questions and the host's alert was still inside its grace hour. A
   * camera with a storage fault goes red on the reading; a host now does too.
   * Only `fail` counts: `unavailable` (a VM has no arrays) and `unknown` are
   * not faults.
   */
  for (const key of HOST_CHECKS) {
    const check = input.checks[key];
    if (check?.outcome === 'fail') {
      return verdict('host_fault', check.reason ?? HOST_CHECK_WORDS[key]);
    }
  }

  const video = input.checks['video_present'];
  if (video?.outcome === 'fail') {
    return verdict('no_video', 'no picture');
  }

  /*
   * The fix. This function tests recording, which its predecessor never did.
   *
   * `unavailable` is NOT a fault and NOT health: it means this question cannot
   * be answered here — an Axis camera has no usable storage API, and an exempt
   * deployment is not meant to record onboard at all. The predecessor guarded
   * only the second of those, with a `no_onboard_storage` flag somebody had to
   * remember to set. Here the driver's capability declaration covers the first
   * and `records_onboard` the second, and the cell reports `unmonitored` rather
   * than quietly going green — or, for an exempt unit with a leftover card,
   * loudly going red.
   */
  const recordsOnboard = input.recordsOnboard !== false;
  // NX answered for this camera (070): it records to NX, and the onboard
  // exemption — which is about the camera's own card — does not mute that.
  const nxAnswered = input.checks['nx_camera_recording'] !== undefined && input.checks['nx_camera_recording']?.outcome !== 'unavailable';

  if (!recordsOnboard && !nxAnswered) {
    // Exempt: both recording checks are masked to `unavailable` before they are
    // read, so a leftover card cannot raise and a missing one cannot either.
    // The critical-alert branch below still runs, because the exemption
    // suppresses NOT_RECORDING and nothing else.
    if (input.openAlertSeverities.includes('critical')) {
      return verdict('critical_alert', 'open critical alert');
    }
    return verdict('unmonitored', 'no onboard recording (exempt)');
  }

  /*
   * A camera NX records (070) answers the recording question through NX, and
   * the onboard checks its driver cannot answer must not make it uncheckable.
   * 🔴 Only when NX ANSWERED: every camera is scheduled every camera check,
   * so an ordinary camera carries `nx_camera_recording` as `unavailable` — and
   * judging it on that hid its SD-card faults (2026-09-30, v2026.09.30-5).
   */
  const judged: ReadonlyArray<(typeof RECORDING_CHECKS)[number]> = nxAnswered
    ? ['nx_camera_recording']
    : ['storage_health', 'recording_fresh'];

  for (const key of judged) {
    const check = input.checks[key];
    if (check?.outcome === 'fail') {
      if (key === 'storage_health') return verdict('storage_fault', `storage reports ${check.valueText ?? 'a fault'}`);
      if (key === 'nx_camera_recording') return verdict('not_recording', 'online in NX, not recording');
      return verdict('not_recording', 'storage healthy, no new footage');
    }
  }

  if (input.openAlertSeverities.includes('critical')) {
    return verdict('critical_alert', 'open critical alert');
  }

  // The system drive (073) warns on the reading, as a host fault faults on it.
  const systemDrive = input.checks['system_drive_capacity'];
  if (systemDrive?.outcome === 'fail') {
    return verdict('host_warning', systemDrive.reason ?? 'the system drive is nearly full');
  }

  const unmonitored = judged.filter(
    (key) => input.checks[key]?.outcome === 'unavailable',
  );
  if (unmonitored.length > 0) {
    return verdict('unmonitored', `cannot be checked: ${unmonitored.join(', ')}`);
  }

  if (input.openAlertSeverities.includes('warning')) {
    return verdict('warning_alert', 'open warning');
  }

  return verdict('ok', 'all checks passing');
}

export const CELL_LABELS: Record<CellState, string> = {
  ok: 'OK',
  fault: 'Fault',
  warning: 'Warning',
  stale: 'No reading',
  unmonitored: 'Not checkable',
  maintenance: 'Maintenance',
  retired: 'Retired',
};

// ── The site line ─────────────────────────────────────────────────────────

/**
 * One site, summarised in words.
 *
 * 🔴 The predecessor's landing page was scannable because every row on it WAS a
 * problem — it listed only the faulted cameras. Grouping by site loses that for
 * free: a site card is a card whether or not anything is wrong with it, and a
 * coloured edge does not say what to send somebody to do. The line earns it
 * back.
 *
 * Counted from the verdicts the cells themselves render, so the line and the
 * grid beneath it cannot disagree.
 */
export interface SiteSummary {
  /** Assets in active service. The only ones this site is judged on. */
  active: number;
  /** Not answering. */
  down: number;
  /** Anything a person would act on — every fault plus every warning. */
  affected: number;
  /**
   * Every asset in active service here is not answering.
   *
   * The distinction the ranking exists for: a wholly dark site is a SITE
   * failure — power, router, link — and is a different problem from N camera
   * faults. HANDOFF-2026-08-27 lists four of them (L84 2-of-2, L177 2-of-2,
   * L32, L139) and draws exactly this line.
   */
  dark: boolean;
  /** The line, in words. Empty when there is nothing to say. */
  line: string;
}

/**
 * In the order a reader should meet them: worst first, quietest last.
 *
 * `outOf` is the site's active count, or null where naming a proportion would
 * be a claim rather than a measurement — see `place` below.
 */
const PHRASE: ReadonlyArray<readonly [CellKind, (n: number, outOf: number | null) => string]> = [
  [
    'unreachable',
    (n, outOf) => (outOf !== null && outOf > n ? `${n} of ${outOf} not answering` : `${n} not answering`),
  ],
  ['no_video', (n) => `${n} answering with no picture`],
  ['storage_fault', (n) => `${n} with a failed card`],
  ['not_recording', (n) => `${n} not recording`],
  ['critical_alert', (n) => `${n} with an open critical`],
  ['host_warning', (n) => `${n} with a nearly full system drive`],
  ['warning_alert', (n) => `${n} with an open warning`],
  // Not a fault and deliberately last: an asset nothing has heard from is not
  // broken, it is unmeasured, and saying so is the whole point of the grey.
  ['stale', (n) => `${n} with no reading`],
];

export function summariseSite(
  assets: readonly { lifecycle: string; verdict: CellVerdict }[],
  /**
   * Is this a place?
   *
   * ⚠️ False for a holding pen — `UNASSIGNED`, where an asset waits for a real
   * site. "3 of 3 not answering" claims a shared cause, and three cameras that
   * share nothing but nobody knowing where they are do not have one. They get
   * the plain count instead, and `dark` stays false, which is also what the
   * server's `rank_dark` says about the same rows.
   */
  place = true,
): SiteSummary {
  const active = assets.filter((a) => a.lifecycle === 'active');
  const tally = new Map<CellKind, number>();
  for (const a of active) tally.set(a.verdict.kind, (tally.get(a.verdict.kind) ?? 0) + 1);

  const count = (kind: CellKind): number => tally.get(kind) ?? 0;
  const down = count('unreachable');
  const affected = active.filter(
    (a) => a.verdict.state === 'fault' || a.verdict.state === 'warning',
  ).length;
  const dark = place && active.length > 0 && down === active.length;

  const parts = dark
    ? [`${down} of ${active.length} not answering`]
    : PHRASE.filter(([kind]) => count(kind) > 0).map(([kind, say]) =>
        say(count(kind), place ? active.length : null),
      );

  return { active: active.length, down, affected, dark, line: parts.join(' · ') };
}

/**
 * The fleet as one site's overview sees it: that site, its assets, and only
 * their checks, alerts and pictures. Everything the fleet screen computes —
 * counters, the card, the device table — then describes the site and nothing
 * else, from the same code and so the same verdicts as the whole-fleet view.
 *
 * Structural rather than importing `Fleet` from api.ts, which keeps this file
 * free of the client's imports and testable on its own.
 */
export function scopeFleetToSite<
  F extends {
    sites: readonly { id: string }[];
    assets: readonly { id: string; site_id: string | null }[];
    checkState: readonly { asset_id: string }[];
    alerts: readonly { asset_id: string }[];
    snapshots?: readonly { asset_id: string }[];
  },
>(fleet: F, siteId: string): F {
  const ids = new Set(fleet.assets.filter((a) => a.site_id === siteId).map((a) => a.id));
  return {
    ...fleet,
    sites: fleet.sites.filter((s) => s.id === siteId),
    assets: fleet.assets.filter((a) => ids.has(a.id)),
    checkState: fleet.checkState.filter((r) => ids.has(r.asset_id)),
    alerts: fleet.alerts.filter((r) => ids.has(r.asset_id)),
    ...(fleet.snapshots !== undefined ? { snapshots: fleet.snapshots.filter((r) => ids.has(r.asset_id)) } : {}),
  };
}

/**
 * A site overview's "needs attention" list: the faults, then the warnings,
 * each by code. Everything else is left to the full grid below it.
 *
 * 🔴 At a site of a hundred and forty cameras across three NX servers the
 * grid is in code order, so the three that are broken sit somewhere in the
 * middle of a page of green. The overview leads with these instead.
 */
export function needingAttention<A extends { id: string; code: string | null; name: string }>(
  assets: readonly A[],
  verdicts: ReadonlyMap<string, { state: CellState; kind: CellKind }>,
): A[] {
  return worstFirst(
    assets.filter((a) => {
      const state = verdicts.get(a.id)?.state;
      return state === 'fault' || state === 'warning';
    }),
    verdicts,
  );
}

// ── Worst first ───────────────────────────────────────────────────────────

/**
 * How urgent each state is, worst first. The device table's status column and
 * `KIND_RANK` below both read it.
 *
 * Stale above unmonitored: an asset that has gone quiet may be the start of a
 * fault, while one whose driver cannot answer a question has been that way
 * since it was installed.
 */
export const STATE_RANK: Record<CellState, number> = {
  fault: 0,
  warning: 1,
  stale: 2,
  unmonitored: 3,
  maintenance: 4,
  ok: 5,
  retired: 6,
};

/**
 * How urgent each kind is, worst first — the order an operator should meet
 * cameras in on a site card.
 *
 * 🔴 The cards drew assets in `ORDER BY a.code`, so at Sandy Point (2026-10-21)
 * a camera not answering at all sat fourth, under its NX server's open warning
 * and two lesser items. At a site of 140 cameras that is an outage below the
 * fold. Inside a state it follows `PHRASE`, the site line's order, so the line
 * and the cards under it name faults in the same order.
 *
 * Grouped by `STATE_RANK` and never across it: every fault outranks every
 * warning. `test/fleet_state.test.ts` holds that.
 */
export const KIND_RANK: Record<CellKind, number> = {
  unreachable: 0,
  no_video: 1,
  storage_fault: 2,
  not_recording: 3,
  host_fault: 4,
  critical_alert: 5,
  host_warning: 10,
  warning_alert: 11,
  stale: 20,
  unmonitored: 30,
  maintenance: 40,
  ok: 50,
  retired: 60,
};

/**
 * Assets worst first, then by code (or name), then id — stable, so a poll that
 * changes nothing moves nothing. An asset with no verdict yet goes last.
 */
export function worstFirst<A extends { id: string; code: string | null; name: string }>(
  assets: readonly A[],
  verdicts: ReadonlyMap<string, { kind: CellKind }>,
): A[] {
  const rank = (a: A): number => {
    const kind = verdicts.get(a.id)?.kind;
    return kind === undefined ? Number.MAX_SAFE_INTEGER : KIND_RANK[kind];
  };
  return [...assets].sort(
    (x, y) =>
      rank(x) - rank(y) ||
      (x.code ?? x.name).localeCompare(y.code ?? y.name) ||
      (x.id < y.id ? -1 : x.id > y.id ? 1 : 0),
  );
}

/**
 * When the site list may be re-sorted: the key `useSteadyOrder` in Fleet.tsx
 * holds the server's order under.
 *
 * 🔴 It was membership alone — which sites have anything open — so a site
 * whose open warning became a dead camera, or whose last camera went dark,
 * kept its old place for as long as the same sites stayed in trouble. The
 * server had already ranked it higher; the screen held it down. A site's place
 * now follows its worst problem: severity and darkness are part of the key,
 * while the counts (which move on every poll) still are not.
 */
export function siteOrderKey(
  sites: readonly { id: string; rank_severity: number; rank_dark: boolean; rank_placeless: boolean }[],
): string {
  return [
    sites
      .filter((s) => !s.rank_placeless && s.rank_severity > 0)
      .map((s) => `${s.id}:${s.rank_severity}${s.rank_dark ? ':dark' : ''}`)
      .sort()
      .join(','),
    // Membership of the whole list too: a site added or retired must be
    // placed, and a held order that no longer covers the list would drop it.
    sites.map((s) => s.id).sort().join(','),
  ].join('|');
}
